import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  TextField,
  Button,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from "recharts";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";

const WEBBIT_TEAL = "#083b4b";

// One distinct colour per STAFF member, reused across all three charts so a given person
// is the same colour everywhere. A categorical palette (not a single-hue gradient) so
// adjacent slices are actually tell-apart-able.
const STAFF_COLORS = [
  "#2563eb", "#16a34a", "#f59e0b", "#dc2626", "#7c3aed", "#0891b2",
  "#db2777", "#65a30d", "#ea580c", "#0d9488", "#9333ea", "#ca8a04",
  "#4f46e5", "#e11d48", "#059669", "#64748b",
];

// Per-metric accent (chart title + table number column) — one representative hue each.
const METRIC_ACCENT = { Packed: "#083b4b", Checked: "#2563eb", Supplied: "#7c3aed" };

function fmtDate(iso) {
  if (!iso) return "";
  const p = String(iso).split("T")[0].split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}` : iso;
}

// Full date, e.g. "12 Mar 2024", for the first-used card.
function fmtLongDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${String(iso).split("T")[0]}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

// Whole months/years since a date, e.g. "1 yr 3 mo ago".
function sinceLabel(iso) {
  if (!iso) return "";
  const start = new Date(`${String(iso).split("T")[0]}T00:00:00`);
  if (Number.isNaN(start.getTime())) return "";
  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 1) return "this month";
  const yrs = Math.floor(months / 12);
  const mos = months % 12;
  const parts = [];
  if (yrs) parts.push(`${yrs} yr${yrs > 1 ? "s" : ""}`);
  if (mos) parts.push(`${mos} mo`);
  return `${parts.join(" ")} ago`;
}

// ── Stat card — identical to other tabs ──────────────────────────────────────
function StatCard({ label, value, sub }) {
  return (
    <Box sx={{
      p: 2,
      borderRadius: 2,
      border: "1px solid #e5e7eb",
      backgroundColor: "#f9fafb",
      display: "flex",
      flexDirection: "column",
      gap: 0.25,
    }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: WEBBIT_TEAL, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && <Typography variant="caption" sx={{ color: "text.secondary" }}>{sub}</Typography>}
    </Box>
  );
}

// ── Mini pie chart ────────────────────────────────────────────────────────────
function MiniPie({ title, data, colorForName, accentColor }) {
  return (
    <Box sx={{
      p: 2,
      borderRadius: 2,
      border: "1px solid #e5e7eb",
      backgroundColor: "#fff",
      boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: accentColor, mb: 0.5 }}>
        {title}
      </Typography>
      {data.length > 0 ? (
        <ResponsiveContainer width="100%" height={140}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="44%"
              outerRadius={48}
              label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
              labelLine={false}
            >
              {data.map((entry, i) => (
                <Cell key={i} fill={colorForName(entry.name)} />
              ))}
            </Pie>
            <Legend iconSize={8} wrapperStyle={{ fontSize: 10, paddingTop: 2 }} formatter={(v) => v} />
            <ReTooltip formatter={(v) => [v, title]} contentStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <Typography sx={{ color: "text.disabled", fontSize: "0.78rem", py: 2, textAlign: "center" }}>
          No data
        </Typography>
      )}
    </Box>
  );
}

// ── Table styles ──────────────────────────────────────────────────────────────
const headerCellSx = {
  fontWeight: 700,
  fontSize: "0.72rem",
  color: "#fff",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  borderBottom: `2px solid ${WEBBIT_TEAL}`,
  backgroundColor: WEBBIT_TEAL,
  py: 0.75,
  px: 1.25,
};

const bodyCellSx = {
  fontSize: "0.82rem",
  color: "#374151",
  borderBottom: "1px solid #f3f4f6",
  py: 0.6,
  px: 1.25,
};

const numCellSx = { ...bodyCellSx, textAlign: "right", fontVariantNumeric: "tabular-nums" };

// ── Main component ────────────────────────────────────────────────────────────
const toYMD = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => toYMD(new Date(Date.now() - n * 24 * 60 * 60 * 1000));

const ChemistWebbitTab = ({ chemistUUID }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [dateStart, setDateStart] = useState(() => daysAgo(30));
  const [dateEnd, setDateEnd]     = useState(() => toYMD(new Date()));

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(API_PATHS.CHEMIST_WEBBIT, "GET", null, {
        ChemistUUID: chemistUUID,
        DateStart: dateStart,
        DateEnd: dateEnd,
      });
      setData(res);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load Webbit data"));
    } finally {
      setLoading(false);
    }
  }, [chemistUUID, dateStart, dateEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setPreset = (days) => { setDateStart(daysAgo(days)); setDateEnd(toYMD(new Date())); };

  if (loading && !data) return (
    <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
      <CircularProgress size={28} sx={{ color: WEBBIT_TEAL }} />
    </Box>
  );

  if (error && !data) return (
    <Box sx={{ py: 4, px: 2 }}>
      <Alert severity="warning">{error}</Alert>
    </Box>
  );

  if (!data) return null;

  const packsPerDay = data.PacksPerDay ?? [];
  const staffTable  = data.StaffTable  ?? [];

  // Collected (direct to customer) vs Delivered split of supplied packs.
  const split = data.SupplySplit ?? { Collected: 0, Delivered: 0 };
  const supplyData = [
    { name: "Collected", value: Number(split.Collected ?? 0) },
    { name: "Delivered", value: Number(split.Delivered ?? 0) },
  ].filter((d) => d.value > 0);
  const supplyColor = (name) => (name === "Delivered" ? "#2563eb" : "#16a34a");
  const supplyTotal = supplyData.reduce((s, d) => s + d.value, 0);

  const pieData = (metric) =>
    staffTable
      .filter((r) => Number(r[metric] ?? 0) > 0)
      .map((r) => ({ name: r.StaffName, value: Number(r[metric]) }));

  // Stable staff → colour map (by name, alphabetical) shared by all three charts.
  const staffNames = [...new Set(staffTable.map((r) => r.StaffName).filter(Boolean))]
    .sort((a, b) => String(a).localeCompare(String(b)));
  const colorByStaff = new Map(staffNames.map((name, i) => [name, STAFF_COLORS[i % STAFF_COLORS.length]]));
  const colorForName = (name) => colorByStaff.get(name) || "#94a3b8";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

      {/* Stat cards — full-width auto-fill grid, easy to add more later */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 1.5 }}>
        <StatCard
          label="Packs supplied"
          value={Number(data.PacksSupplied30d ?? 0).toLocaleString()}
          sub={`~${data.AvgPacksPerDay ?? 0}/day`}
        />
        <StatCard
          label="Active patients"
          value={Number(data.ActivePatients ?? 0).toLocaleString()}
        />
        <StatCard
          label="Webbit started"
          value={fmtLongDate(data.FirstUsedDate)}
          sub={data.FirstUsedDate ? sinceLabel(data.FirstUsedDate) : "no activity yet"}
        />
      </Box>

      {/* Bar chart — full width for readable bars */}
      <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: WEBBIT_TEAL }}>
            Packs per day
          </Typography>
          {/* Date range selectors */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <TextField
              type="date" size="small" label="From"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: dateEnd }}
              sx={{ width: 150, "& .MuiInputBase-input": { fontSize: "0.8rem", py: 0.75 } }}
            />
            <TextField
              type="date" size="small" label="To"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: dateStart, max: toYMD(new Date()) }}
              sx={{ width: 150, "& .MuiInputBase-input": { fontSize: "0.8rem", py: 0.75 } }}
            />
            {[
              { label: "7d", days: 7 },
              { label: "30d", days: 30 },
              { label: "90d", days: 90 },
            ].map((p) => (
              <Button key={p.label} size="small" variant="outlined" onClick={() => setPreset(p.days)}
                sx={{ textTransform: "none", minWidth: 0, px: 1, py: 0.25, fontSize: "0.72rem", borderColor: "#cbd5e1", color: WEBBIT_TEAL }}>
                {p.label}
              </Button>
            ))}
            {loading && <CircularProgress size={14} sx={{ color: WEBBIT_TEAL }} />}
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" onClick={fetchData} disabled={loading} sx={{ color: WEBBIT_TEAL }}>
                  <RefreshIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={packsPerDay} margin={{ top: 4, right: 12, left: -12, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={fmtDate}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} width={32} />
              <ReTooltip
                formatter={(v) => [v, "Packs"]}
                labelFormatter={(v) => `Date: ${fmtDate(v)}`}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
              />
              <Bar dataKey="count" fill="#94d628" radius={[3, 3, 0, 0]} maxBarSize={34} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      {/* Pie row — supply split + staff breakdowns, evenly distributed */}
      {(supplyTotal > 0 || staffTable.length > 0) && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2 }}>
          {supplyTotal > 0 && (
            <MiniPie title="Collected vs Delivered" data={supplyData} colorForName={supplyColor} accentColor={WEBBIT_TEAL} />
          )}
          {staffTable.length > 0 && (
            <>
              <MiniPie title="Packed"   data={pieData("Packed")}   colorForName={colorForName} accentColor={METRIC_ACCENT.Packed} />
              <MiniPie title="Checked"  data={pieData("Checked")}  colorForName={colorForName} accentColor={METRIC_ACCENT.Checked} />
              <MiniPie title="Supplied" data={pieData("Supplied")} colorForName={colorForName} accentColor={METRIC_ACCENT.Supplied} />
            </>
          )}
        </Box>
      )}

      {/* Staff activity table — full width */}
      {staffTable.length > 0 ? (
        <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: WEBBIT_TEAL, mb: 1.5 }}>
            Staff activity
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Staff</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: "right" }}>Packed</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: "right" }}>Checked</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: "right" }}>Supplied</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {staffTable.map((row) => (
                <TableRow
                  key={row.StaffID}
                  sx={{ "&:last-child td": { borderBottom: 0 }, "&:hover td": { backgroundColor: "#f0fdf4" } }}
                >
                  <TableCell sx={bodyCellSx}>{row.StaffName}</TableCell>
                  <TableCell sx={{ ...numCellSx, color: METRIC_ACCENT.Packed, fontWeight: 700 }}>
                    {row.Packed > 0 ? row.Packed : "—"}
                  </TableCell>
                  <TableCell sx={{ ...numCellSx, color: METRIC_ACCENT.Checked, fontWeight: 700 }}>
                    {row.Checked > 0 ? row.Checked : "—"}
                  </TableCell>
                  <TableCell sx={{ ...numCellSx, color: METRIC_ACCENT.Supplied, fontWeight: 700 }}>
                    {row.Supplied > 0 ? row.Supplied : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Box sx={{ p: 3, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ color: "text.disabled", fontSize: "0.85rem" }}>No staff data available.</Typography>
        </Box>
      )}

    </Box>
  );
};

export default ChemistWebbitTab;
